import * as React from 'react';

import {
  enumInventoryPeriodSortOption,
  InventoryPeriodSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  Pagination,
  type PaginationProps,
  Select,
  ListScreenLayout,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideSettings2 } from 'lucide-react';

import { InventoryPeriodRecord } from '../../_graphql/queryInventoryPeriods';
import { $remoteFacilities } from '../../_state/facilities.state';
import * as configState from '../../_state/inventoryPeriodConfig.state';
import * as state from '../../_state/inventoryPeriodsList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { InventoryPeriodConfigDialog } from './InventoryPeriodConfigDialog';
import { useInventoryPeriodsColumns } from './useInventoryPeriodsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: InventoryPeriodSortOption; desc: InventoryPeriodSortOption }
> = {
  name: {
    asc: enumInventoryPeriodSortOption.NameAsc,
    desc: enumInventoryPeriodSortOption.NameDesc,
  },
  startDate: {
    asc: enumInventoryPeriodSortOption.StartDateAsc,
    desc: enumInventoryPeriodSortOption.StartDateDesc,
  },
  endDate: {
    asc: enumInventoryPeriodSortOption.EndDateAsc,
    desc: enumInventoryPeriodSortOption.EndDateDesc,
  },
  status: {
    asc: enumInventoryPeriodSortOption.StatusAsc,
    desc: enumInventoryPeriodSortOption.StatusDesc,
  },
};

export const InventoryPeriodsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedStatus, setSelectedStatus] = useAtom(state.$selectedStatus);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const [isConfigDialogOpen, setIsConfigDialogOpen] = useAtom(
    configState.$isConfigDialogOpen,
  );
  const { data: configData } = useAtomValue(
    configState.$remoteInventoryPeriodConfig,
  );

  const { data, isFetching, loadingState, error } = useAtomValue(
    state.$remoteInventoryPeriods,
  );

  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );

  const total = data?.totalCount ?? 0;
  const periods = data?.periods ?? [];

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInventoryPeriodSortOption.StartDateDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleRowClick = React.useCallback(
    (cell: { row: InventoryPeriodRecord }) => {
      setLocation(`/inventory/periods/${cell.row.id}`);
    },
    [setLocation],
  );

  const columns = useInventoryPeriodsColumns({
    onRowClick: handleRowClick,
  });

  const facilityOptions = React.useMemo(
    () => [
      { key: 'all', label: t.periodsListView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ],
    [facilities, t],
  );

  const statusOptions = React.useMemo(
    () => [
      { key: 'all', label: t.periodsListView.filters.allStatuses },
      { key: 'Open', label: t.periodsListView.statusTypes.Open },
      { key: 'Closed', label: t.periodsListView.statusTypes.Closed },
      { key: 'Locked', label: t.periodsListView.statusTypes.Locked },
      { key: 'Archived', label: t.periodsListView.statusTypes.Archived },
    ],
    [t],
  );

  return (
    <div className="@InventoryPeriodsListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <>
            <Select
              value={selectedFacility || 'all'}
              onChange={(key) => {
                setSelectedFacility(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={facilityOptions}
              className="w-48"
            />
            <Select
              value={selectedStatus || 'all'}
              onChange={(key) => {
                setSelectedStatus(key === 'all' ? undefined : String(key));
                setOffset(0);
              }}
              options={statusOptions}
              className="w-40"
            />
          </>
        }
        actions={
          <Button
            variant="outline"
            color="neutral"
            icon={<LucideSettings2 />}
            onPress={() => setIsConfigDialogOpen(true)}
          >
            {t.periodsListView.periodConfig.configureButton}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<InventoryPeriodRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={periods}
          rowKey="id"
          aria-label={t.periodsListView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.periodsListView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={{
            title: t.periodsListView.emptyState.title,
            description: <span>{t.periodsListView.emptyState.subtitle}</span>,
            centered: true,
          }}
        />
      </ListScreenLayout>

      <InventoryPeriodConfigDialog
        isOpen={isConfigDialogOpen}
        onClose={() => setIsConfigDialogOpen(false)}
        existingConfig={configData}
      />
    </div>
  );
};
